if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        // Add-on products use #custom_addtocart (handled by itg-product.js) and
        // have no [type="submit"] button, so guard against a null submitButton.
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton ? this.submitButton.querySelector('span') : null;

        if (this.submitButton && document.querySelector('cart-drawer'))
          this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';

        // Old-theme behavior: products with a "Size" option require the customer
        // to pick a size before adding to cart (no variant is pre-selected).
        this.isSized = this.dataset.isSized === 'true';
        if (this.isSized) this.setupSizeValidation();
      }

      setupSizeValidation() {
        this.inQuickAddModal = !!this.closest('quick-add-modal');
        const localRoot =
          this.closest('quick-add-modal') || this.closest('product-info') || document;

        // Restore the old theme's error position: directly below the variant
        // options. Inserted as a sibling AFTER <variant-selects> (not inside it)
        // so it survives Dawn 15's section re-render on variant change. The
        // reference is cached so handleErrorMessage() still finds it after the move.
        const variantPicker = localRoot.querySelector('variant-selects, variant-radios');
        this.errorMessageWrapper = this.querySelector('.product-form__error-message-wrapper');
        if (variantPicker && this.errorMessageWrapper) {
          variantPicker.insertAdjacentElement('afterend', this.errorMessageWrapper);
        }

        // Clear the "please select a size" error once a size is chosen.
        // Delegated from a stable root (product-info / modal / document) rather
        // than bound per-input, because Dawn replaces the <variant-selects> DOM
        // on every variant change, which would drop per-input listeners.
        // (Whether a size counts as "chosen" for cart purposes is tracked in
        // product-info.js, which owns the re-render.)
        localRoot.addEventListener('change', (event) => {
          const el = event.target;
          if (!el.matches?.('[data-product-size], select[name="options[Size]" i]')) return;
          if (el.value) this.handleErrorMessage();
        });

        // Block both the standard dynamic-checkout button and the custom add-on
        // cart button (#custom_addtocart, handled by itg-product.js) when no size
        // is selected. Capture phase + stopImmediatePropagation prevents the
        // itg-product.js delegated handler from firing.
        const guardedButtons = this.querySelectorAll('#custom_addtocart, .shopify-payment-button');
        guardedButtons.forEach((btn) => btn.addEventListener('click', this.onDynamicCheckoutClick.bind(this), true));
      }

      onDynamicCheckoutClick(evt) {
        if (this.hasMissingSize()) {
          evt.preventDefault();
          evt.stopImmediatePropagation();
          this.showMissingSizeError();
        }
      }

      hasMissingSize() {
        if (!this.isSized) return false;
        return !this.variantIdInput || !this.variantIdInput.value;
      }

      showMissingSizeError() {
        this.handleErrorMessage('Please select a size before continuing.');
        const localRoot =
          this.closest('quick-add-modal') || this.closest('product-info') || document;
        const sizeEl = localRoot.querySelector('[data-product-size], select[name="options[Size]" i]');
        if (sizeEl && typeof sizeEl.focus === 'function') sizeEl.focus();
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton && this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        // Require a size selection before submitting (old-theme behavior).
        if (this.hasMissingSize()) {
          this.showMissingSizeError();
          return;
        }

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (!this.submitButton) return;
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
